// F4 profile dropdown: name/email header, optional language switcher
// (contributor cabinet only), the F3 cabinet switcher (only when the caller
// has more than one role), "change password", "log out".
//
// Body-level portal, same reasoning as kids Shell's ProfileMenu (see
// apps/kids-web/src/Shell.tsx): a dropdown nested inside the topbar can get
// relocated or clipped by an ancestor's overflow/stacking context, and vista
// has no framework layer managing that for us either. A portal makes the
// menu's z-index and backdrop independent of wherever the trigger button
// happens to sit in the tree.
import { createPortal } from 'preact/compat';
import type { Lang } from '../i18n';

export type SwitchItem = { role: string; label: string };
export type LangItem = { code: Lang; label: string };

type Props = {
  name?: string | null;
  email: string;
  switchItems: SwitchItem[];
  onSwitchRole: (role: string) => void;
  // null прячет строку языка целиком. Штабу она теперь нужна не меньше, чем
  // контрибьютору: кабинет перестал быть русским-только (14.08).
  langItems: LangItem[] | null;
  activeLang?: Lang;
  onSetLang?: (lang: Lang) => void;
  langLabel?: string;
  /** «сборка» рядом с номером бандла. */
  buildLabel?: string;
  passwordLabel: string;
  onChangePassword: () => void;
  logoutLabel: string;
  onLogout: () => void;
  onClose: () => void;
};

export function ProfileMenu({
  name,
  email,
  switchItems,
  onSwitchRole,
  langItems,
  activeLang,
  onSetLang,
  langLabel,
  buildLabel = 'build',
  passwordLabel,
  onChangePassword,
  logoutLabel,
  onLogout,
  onClose,
}: Props) {
  return createPortal(
    <div class="profile-menu-backdrop" onClick={onClose}>
      <div class="profile-menu wb-card" role="menu" onClick={(e: Event) => e.stopPropagation()}>
        <div class="profile-menu-head">
          <div class="profile-menu-name">{name ?? email}</div>
          <div class="profile-menu-email">{email}</div>
          {/* Номер сборки — конец спорам «у меня старая версия или новая»:
              сверяется с vista.weern.com/version.json одним взглядом. */}
          <div class="profile-menu-build">
            {buildLabel} {(import.meta.env.VITE_BUILD_TAG as string | undefined) ?? 'dev'}
          </div>
        </div>

        {langItems && langItems.length > 0 && (
          <div class="profile-menu-item profile-menu-lang-row" role="group" aria-label={langLabel}>
            <span>{langLabel}</span>
            <span class="profile-menu-lang">
              {langItems.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  class={`profile-menu-lang-btn${l.code === activeLang ? ' is-active' : ''}`}
                  aria-pressed={l.code === activeLang}
                  onClick={(e: Event) => {
                    e.stopPropagation();
                    onSetLang?.(l.code);
                  }}
                >
                  {l.label}
                </button>
              ))}
            </span>
          </div>
        )}

        {switchItems.map((it) => (
          <button
            key={it.role}
            type="button"
            class="profile-menu-item"
            role="menuitem"
            onClick={() => {
              onClose();
              onSwitchRole(it.role);
            }}
          >
            <span>{it.label}</span>
            <span class="profile-menu-chev" aria-hidden="true">›</span>
          </button>
        ))}

        <button
          type="button"
          class="profile-menu-item"
          role="menuitem"
          onClick={() => {
            onClose();
            onChangePassword();
          }}
        >
          <span>{passwordLabel}</span>
          <span class="profile-menu-chev" aria-hidden="true">›</span>
        </button>

        <button
          type="button"
          class="profile-menu-item is-danger"
          role="menuitem"
          onClick={() => {
            onClose();
            onLogout();
          }}
        >
          {logoutLabel}
        </button>
      </div>
    </div>,
    document.body,
  );
}
